import { CheckCircle2, Loader2, MessageSquareText, RefreshCw, TriangleAlert } from "lucide-react";
import { NavLink, Outlet, useOutletContext } from "react-router";

import { Badge } from "@/components/ui/badge";
import { useAsync } from "@/hooks/use-async";
import { useAdminCourse, useAdminKey } from "@/hooks/use-auth";
import { adminApi, ApiError, type SlackAuth } from "@/lib/api";
import { cn } from "@/lib/utils";

import { DmComposer } from "./slack-dm";
import { SlackSync } from "./slack-sync";

/**
 * Slack: keep enrollment in step with the workspace, and message students.
 *
 * Two sub-pages under one section rather than a tab control inside the page: the console
 * already has one row of underline tabs, and a second, differently styled row beneath it
 * read as clutter. These are the same underline, one size down, with the connection
 * status on the right where a page title would otherwise be.
 *
 * The course runs one workspace across terms and turns student accounts on and off in it
 * by hand, so the workspace is the roster and there is no channel to name.
 */
export function AdminSlackRoute() {
  const adminKey = useAdminKey();
  const courseId = useAdminCourse();
  // `null` fetcher means idle rather than a request that cannot succeed.
  const auth = useAsync(adminKey ? () => adminApi.slackAuth(adminKey) : null, [adminKey]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b">
        <nav className="-mb-px flex gap-1" aria-label="Slack tools">
          {[
            { to: "/admin/slack", label: "Sync", icon: RefreshCw, end: true },
            { to: "/admin/slack/messages", label: "Messages", icon: MessageSquareText, end: false },
          ].map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-sm whitespace-nowrap transition-colors",
                  isActive ? "border-foreground text-foreground font-medium" : "text-muted-foreground hover:text-foreground border-transparent",
                )
              }
            >
              <Icon className="size-3.5 opacity-70" aria-hidden />
              {label}
            </NavLink>
          ))}
        </nav>
        <TokenStatus loading={auth.loading} error={auth.error} auth={auth.data ?? null} />
      </div>

      {courseId ? (
        // Keyed on the course so switching it remounts the page below, discarding any
        // preview taken against the previous one. A stale preview left on screen is how a
        // change gets confirmed against the wrong cohort.
        <Outlet key={courseId} context={{ courseId, auth: auth.data ?? null } satisfies SlackContext} />
      ) : (
        <p className="text-muted-foreground text-sm">Choose a course above to continue.</p>
      )}
    </div>
  );
}

interface SlackContext {
  courseId: string;
  auth: SlackAuth | null;
}

export function AdminSlackSyncRoute() {
  const { courseId, auth } = useOutletContext<SlackContext>();
  return <SlackSync courseId={courseId} workspaceUrl={auth?.url} />;
}

export function AdminSlackMessagesRoute() {
  const { courseId } = useOutletContext<SlackContext>();
  return <DmComposer courseId={courseId} />;
}

/**
 * Whether Slack is usable at all, checked once and up front.
 *
 * Every other failure here is ambiguous between a bad token and a missing scope.
 * Resolving the first before anything else runs leaves the second as the only
 * remaining explanation.
 */
function TokenStatus({ loading, error, auth }: { loading: boolean; error: unknown; auth: SlackAuth | null }) {
  if (loading) {
    return (
      <p className="text-muted-foreground flex items-center gap-1.5 pb-2 text-xs">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        Checking the Slack token…
      </p>
    );
  }

  if (error) {
    const notConfigured = error instanceof ApiError && error.isNotConfigured;
    return (
      <p className="text-destructive flex items-center gap-1.5 pb-2 text-xs" title={error instanceof ApiError ? error.message : undefined}>
        <TriangleAlert className="size-3.5" aria-hidden />
        {notConfigured ? (
          <>
            Not configured — set <code className="font-mono">SLACK_BOT_TOKEN</code> (scopes: users:read, users:read.email, chat:write, im:write)
          </>
        ) : (
          "Slack rejected the token"
        )}
      </p>
    );
  }

  return (
    <p className="text-muted-foreground flex items-center gap-1.5 pb-2 text-xs">
      <CheckCircle2 className="text-primary size-3.5" aria-hidden />
      <Badge variant="secondary">{auth?.team}</Badge>
      as
      <Badge variant="secondary">{auth?.bot_user}</Badge>
    </p>
  );
}
